/**
 * FireTrack integration.
 *
 * Auto-loads ft.js with the tenant's project key if not already on the page.
 * Provides typed wrappers for ft('track', ...) and ft('identify', ...).
 */

interface FireTrackGlobal {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (action: string, ...args: any[]): void;
  q?: unknown[][];
  initialized?: boolean;
}

declare global {
  interface Window {
    ft?: FireTrackGlobal;
  }
}

export function ensureFireTrack(projectKey: string, scriptUrl: string): void {
  if (typeof window === 'undefined') return;
  if (window.ft?.initialized) return;

  // Install queue shim if not present
  if (!window.ft) {
    const shim = function (this: unknown, ...args: unknown[]) {
      (shim.q = shim.q || []).push(args);
    } as FireTrackGlobal;
    shim.q = [];
    window.ft = shim;
  }

  // Inject script tag (idempotent — don't inject twice)
  const alreadyInjected = document.querySelector(
    `script[data-ft-src="${scriptUrl}"]`,
  );
  if (alreadyInjected) return;

  const s = document.createElement('script');
  s.src = scriptUrl;
  s.async = true;
  s.dataset.project = projectKey;
  s.dataset.ftSrc = scriptUrl;
  document.head.appendChild(s);
}

export function track(eventType: string, payload?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !window.ft) return;
  if (payload) {
    window.ft('track', eventType, payload);
  } else {
    window.ft('track', eventType);
  }
}

export function identify(identity: {
  email?: string;
  phone?: string;
  name?: string;
}): void {
  if (typeof window === 'undefined' || !window.ft) return;
  window.ft('identify', identity);
}

// Ad-network click identifiers. Captured at boot alongside UTMs so they
// ride on every event's `metadata` block and can be persisted onto the
// Booking row at checkout for Google Ads OCA / Meta CAPI uploads from
// the server-side fan-in path.
const CLICK_ID_KEYS = ['gclid', 'fbclid', 'gbraid', 'wbraid', 'msclkid'] as const;

function readCookieValue(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Capture URL attribution on widget boot. Returns metadata ready to attach
 * to every ft.track call.
 *
 * Cookie fallback: when an embed page strips ad-click query params before
 * our (lazy-loaded) bundle reads `window.location` — e.g. Next.js does a
 * `router.replace()` URL cleanup, GTM's URL passthrough, etc. — gtag.js's
 * conversion linker and the Meta Pixel have already stashed the click ID
 * in first-party cookies. Reading those recovers attribution that the URL
 * no longer carries. Loaded synchronously by ft.js / gtag in `<head>`,
 * which run before the widget bundle, so the cookies are present by the
 * time captureAttribution runs.
 */
export function captureAttribution(): Record<string, unknown> {
  if (typeof window === 'undefined') return {};
  const url = new URL(window.location.href);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const utms: Record<string, string> = {};
  for (const k of utmKeys) {
    const v = url.searchParams.get(k);
    if (v) utms[k] = v;
  }
  const clickIds: Record<string, string> = {};
  for (const k of CLICK_ID_KEYS) {
    const v = url.searchParams.get(k);
    if (v) clickIds[k] = v;
  }

  // _gcl_aw is gtag's AdWords cookie. Format: `GCL.<timestamp>.<value>`,
  // where value is a gclid, gbraid, or wbraid — gtag stores all three
  // under one cookie without distinguishing. Surface as gclid because
  // the server's PAID_CLICK_KEYS treats them symmetrically and gclid is
  // the most common case. _fbc is Meta Pixel's; format is
  // `fb.<subdomain_index>.<timestamp>.<fbclid>`.
  if (!clickIds.gclid) {
    const gcl = readCookieValue('_gcl_aw');
    const m = gcl?.match(/^GCL\.\d+\.(.+)$/);
    if (m && m[1]) clickIds.gclid = m[1];
  }
  if (!clickIds.fbclid) {
    const fbc = readCookieValue('_fbc');
    const m = fbc?.match(/^fb\.\d+\.\d+\.(.+)$/);
    if (m && m[1]) clickIds.fbclid = m[1];
  }
  // Strip the query string from the persisted landing page. The widget
  // accepts deep-link prefill params (firstName / lastName / email /
  // phone — see deep-link.ts), and persisting the raw href would carry
  // that PII onto Booking.landingPageUrl and downstream into the
  // FireTrack /v1/events POST. utms + clickIds already capture the
  // marketing-relevant query slice as structured fields, so the bare
  // origin+pathname is enough for "which page did they land on".
  const landingPage = `${url.origin}${url.pathname}`;
  return {
    utms,
    clickIds,
    landingPage,
    referrer: document.referrer || undefined,
  };
}

/**
 * Read the FireTrack visitor cookie (`_ft_uid`). ft.js sets this during
 * its bootstrap IIFE; by the time the widget's checkout call fires it's
 * present synchronously. Returns null when ft.js hasn't loaded yet or
 * the cookie isn't readable (e.g. cross-origin iframe context).
 */
export function readFtVisitorId(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)_ft_uid=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}
